import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { CloudSync, CheckCircle, Error, Sync, Assessment } from '@mui/icons-material';
import * as api from '../services/api';

interface SyncStats {
  clienti_sincronizzati: number;
  ultima_modifica: string;
}

interface LastSync {
  last_sync_timestamp: string;
  formatted: string;
}

interface SyncResult {
  success: boolean;
  message?: string;
  total?: number;
  created?: number;
  updated?: number;
  errors?: any[];
}

const SyncClientiPage: React.FC = () => {
  const [stats, setStats] = useState<SyncStats | null>(null);
  const [lastSync, setLastSync] = useState<LastSync | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const [statsData, lastSyncData] = await Promise.all([
        api.getSyncClientiStats(),
        api.getSyncClientiLastSync(),
      ]);
      setStats(statsData);
      setLastSync(lastSyncData);
    } catch (e: any) {
      setError('Errore nel caricamento dei dati: ' + e.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await api.syncAllClienti();
      setSyncResult(result);
      await loadData();
    } catch (e: any) {
      setError('Errore durante la sincronizzazione: ' + (e.response?.data?.details || e.message));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudSync /> Sync Clienti HRP
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sincronizza l'anagrafica clienti da HRP. Eseguire prima della sincronizzazione progetti.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {syncResult && (
        <Alert
          severity={syncResult.success ? 'success' : 'error'}
          sx={{ mb: 2 }}
          icon={syncResult.success ? <CheckCircle /> : <Error />}
        >
          {syncResult.message} — Totale: {syncResult.total}, Creati: {syncResult.created}, Aggiornati: {syncResult.updated}
          {syncResult.errors && syncResult.errors.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Errori: {syncResult.errors.length}
            </Typography>
          )}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <Assessment fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Clienti sincronizzati
              </Typography>
              <Typography variant="h4">
                {stats ? stats.clienti_sincronizzati : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ultimo sync
              </Typography>
              <Typography variant="body1">
                {lastSync ? lastSync.formatted : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Ultima modifica dati
              </Typography>
              <Typography variant="body1">
                {stats?.ultima_modifica
                  ? new Date(stats.ultima_modifica).toLocaleString('it-IT')
                  : '—'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Sincronizzazione completa</Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Legge tutti i clienti da HRP e aggiorna l'anagrafica locale. L'operazione è idempotente.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <Sync />}
            onClick={handleSyncAll}
            disabled={syncing}
          >
            {syncing ? 'Sincronizzazione...' : 'Sincronizza clienti'}
          </Button>
          {syncing && (
            <Chip label="In corso..." color="info" size="small" />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default SyncClientiPage;
